import { Badge } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Marks a quote or invoice that came from the Xero history import rather than
 * being created in EMERGE.
 *
 * ⚠️ **ONE COMPONENT, FOUR MOUNT POINTS** — both detail pages and both list
 * rows. Two copies would agree the day they were written and drift on the first
 * correction, and what would drift is a claim about where a money document came
 * from. Same reasoning as `ResendApprovalControl`.
 *
 * ══ WHAT THE FIELD MEANS, AND THE TWO FIELDS THAT NEARLY MEAN IT ══════════
 *
 * `importedFromXeroAt` is `null` for everything created in EMERGE and carries
 * the import moment for the 1,127 documents the importer wrote. Migration 130
 * added it rather than deriving the answer, because the two obvious derivations
 * are both wrong in the same silent way:
 *
 *   the `xero` link envelope  says THIS IS IN XERO — equally true of a document
 *                             raised here and pushed, so it cannot separate them
 *   `createdBy === null`      says NO ACTOR WAS RECORDED — about who, not about
 *                             where from, and any future actor-less insert joins
 *                             the set with nothing failing
 *
 * ══ WHY IT IS `neutral` AND NOT A WARNING ═════════════════════════════════
 *
 * ⚠️ An imported document is not a problem and must not look like one. It is
 * real history that a customer was really billed for. `danger` or `warning`
 * beside a genuine refusal would make the two indistinguishable, and this would
 * be on the majority of documents in the system — a tone that shouts on 1,127
 * records teaches people to ignore the tone.
 *
 * The date sits on `title` rather than in the label: which run imported a
 * document matters when somebody is investigating one, and never when they are
 * scanning a list.
 */
export function ImportedFromXeroBadge({ importedAt }: { importedAt: string | null }) {
  if (importedAt === null) return null;

  return (
    <Badge tone="neutral" variant="outline" title={`Imported from Xero on ${formatDateTime(importedAt)}`}>
      From Xero
    </Badge>
  );
}
